import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Landmark } from "lucide-react";
import { listLauraAudits, deleteLauraAudit } from "@/app/lib/auditflowApi";
import type { LauraAudit } from "@/app/components/shared/types";
import { LauraNewAuditModal } from "@/app/components/laura/LauraNewAuditModal";
import { useTranslation } from "@/app/i18n/LocaleContext";

interface Props {
    projectId?: string;
}

export function LauraAuditList({ projectId }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [audits, setAudits] = useState<LauraAudit[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    function load() {
        setLoading(true);
        listLauraAudits(projectId)
            .then(setAudits)
            .catch(() => setAudits([]))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        load();
    }, [projectId]);

    function openAudit(id: string) {
        if (projectId) navigate(`/projects/${projectId}/laura/${id}`);
        else navigate(`/laura/${id}`);
    }

    async function handleDelete(id: string, e: React.MouseEvent) {
        e.stopPropagation();
        if (!confirm("Delete this audit?")) return;
        await deleteLauraAudit(id);
        load();
    }

    return (
        <div className="flex h-full flex-col">
            <header className="flex items-center justify-between border-b px-6 py-4">
                <div className="flex items-center gap-2">
                    <Landmark className="h-5 w-5 text-gray-700" />
                    <h1 className="text-xl font-medium">{t("laura", "title")}</h1>
                </div>
                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white"
                >
                    <Plus className="h-4 w-4" />
                    {t("laura", "newAudit")}
                </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                ) : audits.length === 0 ? (
                    <p className="text-center text-sm text-gray-500 py-12">
                        {t("laura", "empty")}
                    </p>
                ) : (
                    <ul className="divide-y rounded-lg border">
                        {audits.map((a) => (
                            <li
                                key={a.id}
                                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                            >
                                <button
                                    type="button"
                                    onClick={() => openAudit(a.id)}
                                    className="min-w-0 flex-1 text-left"
                                >
                                    <p className="font-medium text-gray-900">{a.title}</p>
                                    <p className="text-xs text-gray-500">
                                        {a.laura_sai_profiles?.name} ·{" "}
                                        {a.current_stage.toUpperCase()} ·{" "}
                                        {new Date(a.updated_at).toLocaleDateString()}
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => handleDelete(a.id, e)}
                                    className="ml-4 shrink-0 text-xs text-red-600 hover:underline"
                                >
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <LauraNewAuditModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={openAudit}
                projectId={projectId}
            />
        </div>
    );
}
